/** @jsxImportSource @emotion/react */
import React from "react";
import { css, useTheme } from '@emotion/react';

interface InteractiveHoverButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    firstContent?: string;
    secondaryContent?: string;
  }

// Emotion CSS styles
const buttonStyles = css`
  position: relative;
  width: auto;
  cursor: pointer;
  overflow: hidden;
  border-radius: 9999px;
  /* Colors will be themed via overrides */
  padding: 0.5rem 1.5rem;
  text-align: center;
  font-weight: 600;

  &:hover .indicator {
    transform: scale(1.008);
  }

  &:hover .primary-text {
    transform: translateX(3rem);
    opacity: 0;
  }

  &:hover .secondary-content {
    transform: translateX(-1.25rem);
    opacity: 1;
  }
`;

const contentStyles = css`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const indicatorStyles = css`
  height: 0.5rem;
  width: 0.5rem;
  border-radius: 9999px;
  /* Color will be themed via override */
  transition: all 300ms ease;
`;

const primaryTextStyles = css`
  display: inline-block;
  transition: all 300ms ease;
`;

const secondaryContentStyles = css`
  position: absolute;
  top: 0;
  z-index: 10;
  display: flex;
  height: 100%;
  width: 100%;
  transform: translateX(3rem);
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  /* Color will be themed via override */
  opacity: 0;
  transition: all 300ms ease;
`;

const arrowStyles = css`
  width: 1rem;
  height: 1rem;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
`;

// Arrow Right Icon component
const ArrowRight = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    css={arrowStyles}
  >
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

export const AnimatedButton = React.forwardRef<
  HTMLButtonElement,
  InteractiveHoverButtonProps
>(({ firstContent, secondaryContent, className, ...props }, ref) => {
  const theme = useTheme() as any;

  const themedButtonStyles = css`
    border: 1px solid ${theme?.palette?.line};
    background: ${theme?.palette?.secondary?.contrast};
    color: ${theme?.palette?.text?.primary};
  `;

  const themedPrimaryTextStyles = css`
    color: ${theme?.palette?.text?.primary};
  `;

  const themedIndicatorStyles = css`
    background: ${theme?.palette?.primary?.main};
  `;

  const themedSecondaryContentStyles = css`
    color: ${theme?.palette?.text?.primary};
  `;

  return (
    <button
      ref={ref}
      css={[buttonStyles, themedButtonStyles, className && css`${className}`]}
      {...props}
    >
      <div css={contentStyles}>
        {/* <div css={[indicatorStyles, themedIndicatorStyles]} className="indicator" /> */}
        <span css={[primaryTextStyles, themedPrimaryTextStyles]} className="primary-text">
          {firstContent}
        </span>
      </div>
      <div css={[secondaryContentStyles, themedSecondaryContentStyles]} className="secondary-content">
        <span>{secondaryContent}</span>
        <ArrowRight />
      </div>
    </button>
  );
});

AnimatedButton.displayName = "InteractiveHoverButton";